import React from 'react';
import {
  RadioButtonGroup,
  RadioButton,
  Tag,
  TextArea,
  TextInput,
  Checkbox,
  InlineNotification,
  Link
} from 'carbon-components-react';
// import { FormError } from 'components';

export type AuthoriseMode = 'add-specific-users' | 'match-email-domains';

export interface AuthoriseStepValue {
  mode: AuthoriseMode;
  existingEmails: string[];
  existingDomains: string[];
  emails: string;
  domains: string;
  confirmed: boolean;
  isExisting: boolean;
}

interface Props {
  value: AuthoriseStepValue;
  onChange: (value: AuthoriseStepValue) => void;
  errorMessage?: string;
}

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const isValidDomain = (domain: string) =>
  /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/.test(
    domain.trim()
  );

const AuthoriseStep: React.FC<Props> = ({ value, onChange, errorMessage }) => {
  const {
    mode,
    emails,
    domains,
    confirmed,
    isExisting,
    existingEmails,
    existingDomains
  } = value;

  const update = (patch: Partial<AuthoriseStepValue>) =>
    onChange({ ...value, ...patch });

  const handleModeChange = (newMode: AuthoriseMode) => {
    update({
      mode: newMode,
      emails: '',
      domains: ''
    });
  };

  const emailsInvalid = !!emails && !emails.split(',').every(isValidEmail);

  const domainsInvalid = !!domains && !domains.split(',').every(isValidDomain);

  return (
    <div className="authorise-step">
      <div className="authorise-step--heading">Authorise users or groups</div>
      <div className="authorise-step--subheading">
        Choose who in your organisation can use this transaction bundle.
      </div>

      <div className="authorise-step--card">
        <div className="authorise-step--radio-group">
          <RadioButtonGroup
            name="authorise-mode"
            valueSelected={mode}
            orientation="vertical"
            onChange={handleModeChange}
          >
            <RadioButton
              id="authorise-add-specific-users"
              labelText="Add specific users - Allow selected users to access and use the purchased transactions."
              value="add-specific-users"
            />
            <RadioButton
              id="authorise-match-email-domains"
              labelText="Match email domains - Allow anyone with an email address from specified domains to use the purchased transactions."
              value="match-email-domains"
            />
          </RadioButtonGroup>
        </div>

        {!isExisting && (
          <InlineNotification
            kind="warning"
            lowContrast={true}
            title=""
            subtitle="The users or email domains you select cannot be changed after you confirm your purchase. If you need to give access to different users later, you'll need to contact BYDA Support or purchase an additional transaction bundle."
            hideCloseButton={true}
            className="authorise-step--warning"
          />
        )}

        {isExisting &&
          (existingEmails.length > 0 || existingDomains.length > 0) && (
            <>
              <InlineNotification
                kind="info"
                lowContrast={true}
                title=""
                subtitle={`These email ${
                  mode === 'add-specific-users' ? 'addresses' : 'domains'
                } are carried over from your previous order. You can add or remove ${
                  mode === 'add-specific-users' ? 'addresses' : 'domains'
                } before confirming.`}
                hideCloseButton={true}
                className="authorise-step--warning"
              />

              <div className="authorise-step--existing-section">
                <div className="authorise-step-existing-section-label bx--label">
                  {mode === 'add-specific-users' ? (
                    <label>Existing specific user(s)</label>
                  ) : (
                    <label>Existing email matching domain(s)</label>
                  )}
                </div>
                <div className="authorise-step--existing-section-values">
                  {mode === 'add-specific-users' ? (
                    <div className="authorise-step--existing-section-emails">
                      {existingEmails.map((email) => (
                        <Tag
                          key={email}
                          type="gray"
                          filter={true}
                          onClose={() =>
                            update({
                              existingEmails: existingEmails.filter(
                                (e) => e !== email
                              )
                            })
                          }
                        >
                          {email}
                        </Tag>
                      ))}
                    </div>
                  ) : (
                    <div className="authorise-step--existing-section-domains">
                      {existingDomains.map((domain) => (
                        <Tag
                          key={domain}
                          type="gray"
                          filter={true}
                          onClose={() =>
                            update({
                              existingDomains: existingDomains.filter(
                                (d) => d !== domain
                              )
                            })
                          }
                        >
                          {domain}
                        </Tag>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

        <div className="authorise-step--input-section">
          {mode === 'add-specific-users' ? (
            <div className="authorise-step--textarea-wrapper">
              <TextArea
                id="authorise-emails"
                labelText="Any specific user(s)"
                placeholder="e.g. user@company.com, another@company.com"
                value={emails}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  update({ emails: e.target.value })
                }
                invalid={emailsInvalid || !!errorMessage}
                invalidText={
                  errorMessage || (emailsInvalid ? 'Invalid email address' : '')
                }
                rows={4}
              />
              {!emailsInvalid && !errorMessage && (
                <p className="authorise-step--helper-text">
                  Use commas to separate multiples
                </p>
              )}
            </div>
          ) : (
            <div className="authorise-step--input-wrapper">
              <TextInput
                id="authorise-domains"
                labelText="Any email matching domain(s)"
                placeholder="domains"
                value={domains}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  update({ domains: e.target.value })
                }
                invalid={domainsInvalid || !!errorMessage}
                invalidText={
                  errorMessage || (domainsInvalid ? 'Invalid domain' : '')
                }
              />
              {!domainsInvalid && !errorMessage && (
                <p className="authorise-step--helper-text">
                  Example: company.com, organisation.com
                </p>
              )}
            </div>
          )}
        </div>

        {!isExisting && (
          <div className="authorise-step--confirm">
            <Checkbox
              id="authorise-confirm"
              labelText={
                <>
                  I&apos;ve checked all email{' '}
                  {mode === 'add-specific-users' ? 'addresses' : 'domains'},
                  confirm the list is correct, and agree to the{' '}
                  <Link
                    target="_blank"
                    href="https://www.byda.com.au/api-services-agreement/"
                  >
                    Terms of Use
                  </Link>
                  .
                </>
              }
              checked={confirmed}
              onChange={(checked: boolean) => update({ confirmed: checked })}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthoriseStep;

export const isAuthoriseStepValid = (value: AuthoriseStepValue): boolean => {
  if (!value.confirmed) return false;
  if (value.mode === 'add-specific-users') {
    if (value.isExisting) {
      const newEmails = value.emails
        ? value.emails.split(',').map((email) => email.trim())
        : [];
      const allEmails = [...value.existingEmails, ...newEmails];
      return allEmails.length > 0 && allEmails.every(isValidEmail);
    }
    return !!value.emails.trim() && value.emails.split(',').every(isValidEmail);
  }
  if (value.isExisting) {
    const newDomains = value.domains
      ? value.domains.split(',').map((domain) => domain.trim())
      : [];
    const allDomains = [...value.existingDomains, ...newDomains];
    return allDomains.length > 0 && allDomains.every(isValidDomain);
  }
  return (
    !!value.domains.trim() && value.domains.split(',').every(isValidDomain)
  );
};
